import React, { useState, useEffect } from "react";
import {Container, Button, Typography, Grid, Box, List, ListItem, Breadcrumbs} from "@mui/material";
import { useParams, NavLink } from 'react-router-dom'
import {makeStyles} from "@mui/styles";
import LightGallery from 'lightgallery/react';
import {requestApi} from "../utils/utils";
import {parseISO, format} from "date-fns";
import {Skeleton} from "@mui/material";

// import styles
import 'lightgallery/css/lightgallery.css';
import 'lightgallery/css/lg-thumbnail.css';

// import plugins if you need
import lgThumbnail from 'lightgallery/plugins/thumbnail';

const useStyles = makeStyles((theme) => ({
    hero: {
        minHeight: '400px',
        height: '80vh',
		position: 'relative',
		display: 'flex',
		alignItems: 'flex-end',
		'@media (max-width: 600px)': {
			 minHeight: '450px',
		}
    },
	heroBackground: {
		position: 'absolute',
		top: 0,
		left: 0,
		width: '100%',
		height: '100%',
		overflow: 'hidden',
	},
	image: {
		position: 'absolute',
		top: 0,
		left: 0,
		objectFit: 'cover',
		width: '100%',
		height: '100% !important',
		zIndex: 2,
		transition: 'all .3s ease'
	},
	skeletonHidden: {
		opacity: 0,
		visibility: 'hidden'
	},
	videoBackground: {
		position: 'absolute',
		left: 0,
		width: '100%',
		height: '310%',
		top: '-100%',
		'@media (max-width: 1300px)': {
			height: '300%',
		},

		'@media (max-width: 1200px)': {
			height: '100%',
			top: '0',
			width: '300%',
			left: '-100%'
		},
	},
	iframe: {
		position: 'absolute',
		top: 0,
		left: 0,
		width: '100%',
		height: '100%',
		pointerEvents: 'none'
	},
	heroContent: {
		position: 'relative',
		bottom: 0,
		zIndex: 3,
		transform: 'translateY(50%)',
		maxWidth: '380px',
		width: '100%',
		margin: '0 auto',
		padding: '15px',
		backgroundColor: '#000',
		color: '#fff',
		'@media (max-width: 1024px)': {
			display: 'none',
		},
	},
	heroContentMobile: {
		transform: 'none',
		padding: 0,
		color: '#000',
		fontSize: '3rem',
		maxWidth: '500px',
		bottom: 'none',
		background: 'transparent',
		display: 'none',
		margin: '20px auto 0px auto',
		'@media (max-width: 1024px)': {
			display: 'block',
		},
	},
	breadcrumbs: {
		padding: '0px 48px',
		fontWeight: '600',
		'@media (max-width: 1024px)': {
			padding: '0px 20px'
		}
	},
	infoList: {
		display: 'grid',
		gridTemplateColumns: 'repeat(2, 1fr)',
		maxWidth: '380px',
		width: '100%',
		margin: '0 auto 20px auto !important',
	},
	counter: {
		display: 'block',
		fontWeight: 800,
		fontSize: '20px',
		marginTop: '5px',
	},
	star: {
		color: '#00A9D9',
		display: 'inline-block',
		marginLeft: '7px',
		fontSize: '10px',
		position: 'relative',
		top: '-3px'
	},
	progressGroup: {
		display: 'grid',
		gridTemplateColumns: 'repeat(2, 1fr)',
		columnGap: '20px',
		'@media (max-width: 700px)': {
			gridTemplateColumns: '1fr',
		}
	},
	progress: {
		width: '100%',
		height: '10px',
		borderRadius: '5px',
		backgroundColor: '#E1E1E1',
		overflow: 'hidden'
	},
	progressFill: {
		display: 'block',
		width: '0%',
		height: '10px',
		borderRadius: '5px',
		position: 'relative',
		left: 0,
		backgroundColor: '#00A9D9',
		transition: 'all 0.8s linear'
	},

	gallery: {
		display: 'grid',
		gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
		gap: '20px'
	},

	galleryItem: {
		cursor: 'pointer',
		minHeight: '120px',
	},

	figure: {
		position: 'relative',
		height: '100%',
		width: '100%',
		margin: 0,
		display: 'block'
	},
	button: {
        padding: ' 10px !important',
		width: '100%',
		display: 'flex !important',
		justifyContent: 'center',
		backgroundColor: '#00A9D9 !important',
		textTransform: 'uppercase',
		color: '#fff !important',
		fontWeight: 600,
		borderRadius: '30px !important',
		marginBottom: '20px !important',
		fontSize: '14px !important',
		maxWidth: '220px',
		marginLeft: 'auto !important',
		marginRight: 'auto !important',
		textDecoration: 'none',
		lineHeight: '1.75 !important',
		letterSpacing: '0.02857em !important',
		fontFamily: '"Roboto","Helvetica","Arial",sans-serif',
		"&:hover": {
			backgroundColor: '#0082A6 !important',
		}
    },
}));


const formatLabel = (key) => {
	const words = key.split('_');
	const formattedWords = words.map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
	return formattedWords;
};

const RatingItem = ({ label, value }) => {
	const classes = useStyles();
	const [widthR, setWidthR] = useState(0);

	useEffect(() => {
		setTimeout(() => {
			setWidthR(parseFloat(value) / 5 * 100);
		}, 500)
	}, [value]);

	return (
		<ListItem sx={{ padding: 0, marginBottom: '25px', alignItems: 'center', '@media (max-width: 700px)': { justifyContent: 'center' } }}>
			<Typography variant="label" component="label" sx={{ fontSize: '14px', flex: '0 0 120px' }}>
				{label}
			</Typography>
			<div className={classes.progress} style={{ maxWidth: '200px' }}>
				<span className={classes.progressFill} style={{width: `${widthR}%`}}/>
			</div>
			<span style={{ display: 'block', textAlign: 'right', flex: '0 0 19px', marginLeft: '10px', whiteSpace: 'nowrap', fontSize: '14px' }}>
				{value !== 0 ? value : '0.0'}
			</span>
		</ListItem>
)
};



const ReviewsPage = () => {
	const [general, setGeneral] = useState([]);
	const [reviews, setReviews] = useState([]);
	const [videoPath, setVideoPath] = useState(null);
	const [imagePath, setImagePath] = useState(null);

	const [currentPageReviews, setCurrentPageReviews] = useState(1);
	const [lastPageReviews, setLastPageReviews] = useState(0);

	const [videoReady, setVideoReady] = useState(false);
	const classes = useStyles();
	const {name} = useParams();

	useEffect(() => {
		document.querySelector('.header').classList.add('transparent');

		return () => {
		document.querySelector('.header').classList.remove('transparent');
		};
	}, []);

	useEffect(async () => {
		const {data} = await requestApi(`/rating/get-reviews?tripClass=${name}`);
		if ('data' in data) {
			setGeneral(data['data']['general']);
			setReviews(data['data']['comments']['data']);
			setCurrentPageReviews(data['data']['comments']['current_page']);
			setLastPageReviews(data['data']['comments']['last_page']);
			data['data']['general']['header'] !== null && setImagePath(data['data']['general']['header']);
			data['data']['general']['video'] !== null && setVideoPath(data['data']['general']['video'].slice(-11));
		}
	}, [name]);


	const loadMore = async () => {
		const {data} = await requestApi(`/rating/get-reviews?tripClass=${name}&page=${currentPageReviews + 1}`);
		if ('data' in data) {
			setReviews([...reviews, ...data['data']['comments']['data']]);
			setCurrentPageReviews(data['data']['comments']['current_page']);
		}
	}


	return (
		<>
			<div className={classes.hero}>
				<div className={classes.heroBackground}>
					<Skeleton variant="rectangular" className={`${classes.image} ${videoReady ? classes.skeletonHidden : ''}`}/>
					{
						videoPath !== null ?
						<div className={classes.videoBackground}>
							<iframe
								src={`https://www.youtube.com/embed/${videoPath}?controls=0&autoplay=1&mute=1&showinfo=0&rel=0&playlist=${videoPath}&loop=1`}
								allowFullScreen
								allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share;"
								onLoad={() => setVideoReady(true)}
								className={classes.iframe}
							></iframe>
						</div> :
						<img
							src={imagePath}
							alt={general.title}
							className={classes.image}
						/>
					}

				</div>
				{general?.title &&
				<Box className={classes.heroContent}>
					<Typography variant="h2" component="h1" align="center" style={{fontWeight: '700', marginBottom: '3px', textTransform: 'uppercase', fontSize: '2rem', letterSpacing: '0.08em', lineHeight: 'unset'}}>{general.title}</Typography>
				</Box>
				}
			</div>
			<div style={{backgroundColor: '#F2F2F2', minHeight: '64px', display: 'flex', alignItems: 'center'}}>
				{general?.title ?
					<Breadcrumbs className={classes.breadcrumbs} aria-label="breadcrumb" separator={'>'}>
						<NavLink underline="hover" style={{textDecoration: 'none', fontWeight: '600', color: '#4781D1'}}to="/ratings">
							Reviews
						</NavLink>
						<Typography color="text.primary" style={{fontWeight: '600'}}>
							{general.title}
						</Typography>
					</Breadcrumbs> :
					<Skeleton variant="rectangular" style={{width: '100%', height: '100%'}}/>
				}
				
			</div>
			<Box className={`${classes.heroContent} ${classes.heroContentMobile}`}>
				<Typography variant="h2" component="h1" align="center" style={{fontWeight: '700', marginBottom: '3px', textTransform: 'uppercase', fontSize: '2rem', letterSpacing: '0.08em', lineHeight: 'unset'}}>{general.title}</Typography>
			</Box>
			<Container maxWidth="lg" sx={{padding: '10px 1.6rem 30px 1.6rem !important', maxWidth: '1100px !important'}}>
				{general.description !== null && <Typography variant="h4" component="h3" align="center" style={{fontSize: '1.8rem', letterSpacing: '0.1em'}}>{general.description}</Typography>}
				<List className={classes.infoList}>
					<ListItem sx={{justifyContent: 'center', padding: '15px 10px', borderRight: '1px solid #BBBBBB'}}>
						<Typography
							gutterBottom
							variant="h3"
							component="h4"
							sx={{
								fontSize: '22px',
								margin: 0,
								fontWeight: '300'
							}}
						>
            	        	Total Reviews
            	        	<span className={classes.counter}>{general.total_reviews}</span>
            	        </Typography>
					</ListItem>
					<ListItem sx={{justifyContent: 'center', padding: '15px 10px'}}>
						<Typography
							variant="h3"
							component="h4"
							sx={{
								fontSize: '22px',
								margin: 0,
								fontWeight: '300'
							}}
						>
							Average Rating
							<span className={classes.counter}>{general.avg}<span className={classes.star + " fa fa-star"}></span></span>
						</Typography>
					</ListItem>
				</List>
				<List className={classes.progressGroup} sx={{marginBottom: '20px', '@media (max-width: 600px)': {margin: '0 auto'}}}>
					{general?.ratings && general.ratings !== undefined && Object.keys(general.ratings).map(rating => <RatingItem label={formatLabel(rating)} value={general.ratings[rating]}/>)}
				</List>
				{general?.guest_images && general.guest_images.length > 0 &&
					<Box sx={{marginBottom: 5, padding: 0}}>
						<Typography variant="h3" component="h4"
							sx={{
								fontSize: '22px !important',
								margin: '0 0 20px 0',
								fontWeight: '300',
								textAlign: 'left',
								'@media (max-width: 599px)': {
									textAlign: 'center'
								}
							}}
						>
							<span style={{borderBottom: '1px solid #000'}}>Guest Photos</span>
						</Typography>
						<LightGallery
							speed={500}
							plugins={[lgThumbnail]}
							elementClassNames={classes.gallery}
						>
							{general.guest_images.slice(-10).map((item, index) => (
								<div className={classes.galleryItem} key={index} data-src={item}>
									<figure className={classes.figure}>
										<img src={item} alt={`${general.title} ${index + 1}`} className={classes.image}/>
									</figure>
								</div>
							))}
						</LightGallery>
					</Box>
				}
				{reviews.length > 0 ?
					<>
						<Box sx={{marginBottom: 3, padding: 0}}>
							<Typography variant="h3" component="h4"
								sx={{
									fontSize: '22px !important',
									margin: 0,
									fontWeight: '300',
									textAlign: 'left',
									'@media (max-width: 599px)': {
										textAlign: 'center'
									}
								}}
							>
								<span style={{borderBottom: '1px solid #000'}}>Guest Reviews</span>
							</Typography>
							{reviews.map((item, index) => (
								<Grid key={index} container className="review-card">
									<Grid item sm={2} xs={12} className="review-card__header">
										<Typography variant="h5" component="h5"
											sx={{
												fontSize: '16px',
												margin: 0,
												fontWeight: '700',
											}}
										>
											{item.clientName ? item.clientName : 'Guest'}
										</Typography>
										{item?.overall &&
											<div className="review-card__rating mobile">
												<div className="review-card__rating-star" style={{width: `${parseFloat(item.overall) / 5 * 4.95}em`}}></div>
											</div>
										}
										{item?.tripShip &&
											<Typography variant="h6" component="h6"
												sx={{
													fontSize: '14px',
													margin: 0,
													'@media (max-width: 599px)': {
														display: 'none'
													}
												}}
											>
												{item.tripShip}
											</Typography>
										}
										<Typography variant="h6" component="h6"
											sx={{
												fontSize: '14px',
												margin: 0,
												color: '#999',
												'@media (max-width: 599px)': {
													flex: '1 1 auto',
													display: 'flex',
													justifyContent: 'flex-end',
												}
											}}
										>
											{format(parseISO(item.submitted.slice(0, 10)), 'MMMM d, yyyy')}
										</Typography>
									</Grid>
									<Typography variant="h6" component="h6"
										sx={{
											display: 'none',
											fontSize: '14px',
											margin: '0 0 20px 0',
											fontWeight: '300',
											color: '#999',
											'@media (max-width: 599px)': {
												display: 'block',
											}
										}}
									>
										{item.tripShip}
									</Typography>
									<Grid item sm={10} xs={12} sx={{'@media (min-width: 600px)': {paddingLeft: '40px'}}}>
										{item?.overall &&
											<div className="review-card__rating desktop">
												<div className="review-card__rating-star" style={{width: `${parseFloat(item.overall) / 5 * 4.95}em`}}></div>
											</div>
										}
										{item?.filtered_comments?.length > 0 &&
											<>
												<Typography variant="h5" component="h5"
													sx={{
														fontSize: '16px',
														margin: 0,
														fontWeight: '600',
													}}
												>
													Review:
												</Typography>
												<Box sx={{marginBottom: 2, fontWeight: '300', letterSpacing: '0.01em'}}>{item.filtered_comments}</Box>
											</>
										}
										{item?.filtered_staff?.length > 0 &&
											<>
												<Typography variant="h5" component="h5"
													sx={{
														fontSize: '16px',
														margin: 0,
														fontWeight: '600',
													}}
												>
													Staff standout:
												</Typography>
												<Box sx={{marginBottom: 2, fontWeight: '300', letterSpacing: '0.01em'}}>{item.filtered_staff}</Box>
											</>
										}
										{item?.guest_images.length > 0 &&
											<>
												<Typography variant="h5" component="h5"
													sx={{
														fontSize: '16px',
														margin: '0 0 5px 0',
														fontWeight: '600',
													}}
												>
													Guest photos:
												</Typography>
												<Box sx={{padding: 0}}>
													<LightGallery
														speed={500}
														plugins={[lgThumbnail]}
														elementClassNames={classes.gallery}
													>
														{
															item.guest_images.map((item, index) => (
																<div className={classes.galleryItem} key={index} data-src={item}>
																	<figure className={classes.figure}>
																		<img src={item} alt={`${general.title} ${index + 1}`} className={classes.image}/>
																	</figure>
																</div>
															))
														}
													</LightGallery>
												</Box>
											</>
										}
									</Grid>
								</Grid>
							))}
						</Box>
						<Grid container justifyContent={'space-between'} sx={{margin: '0 auto', maxWidth: '900px'}}>
							{currentPageReviews < lastPageReviews ?
								<>
									<Grid item sm={4} xs={12}>
										<Button className={classes.button} onClick={() => loadMore(reviews.current_page + 1)}>
											LOAD MORE REVIEWS
										</Button>
									</Grid>
									<Grid item sm={4} xs={12}>
										<NavLink
											to={`/?tripClass=${general.shortname}`}
											className={classes.button}
										>
											Dates & Rates
										</NavLink>
									</Grid>
									<Grid item sm={4} xs={12}>
										<NavLink
											to="/ratings"
											className={classes.button}
										>
											All reviews
										</NavLink>
									</Grid>
								</> :
								<>
									<Grid item sm={4} xs={12}>
										<NavLink
											to={`/?tripClass=${general.shortname}`}
											className={classes.button}
										>
											Dates & Rates
										</NavLink>
									</Grid>
									<Grid item sm={6} xs={12}>
										<NavLink
											to="/ratings"
											className={classes.button}
										>
											All reviews
										</NavLink>
									</Grid>
								</>
							}
						</Grid>
					</>
					:
					<Box sx={{marginBottom: 3, padding: 0}}>
						<Typography variant="h3" component="h4"
							sx={{
								fontSize: '22px !important',
								margin: 0,
								fontWeight: '300',
								textAlign: 'left',
								'@media (max-width: 599px)': {
									textAlign: 'center'
								}
							}}
						>
							<span style={{borderBottom: '1px solid #000'}}>Guest Reviews</span>
						</Typography>
						<Box sx={{padding: 0, textAlign: 'center', marginTop: '20px', fontWeight: 600, fontSize: '2.2rem'}}>Coming Soon</Box>
					</Box>
				}
			</Container>
		</>
	)
};

export default ReviewsPage;